import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { iniciales, avaColor } from './helpers';

const W = 344;      // ancho fijo del popover
const MAXH = 384;   // alto máximo (con scroll interno)

/* Popover anclado a un elemento (rect en coords de viewport). Se abre por hover
 * (o clic en touch) y permanece mientras el puntero esté encima (onPointerStay /
 * onPointerLeave los maneja el padre con un timer). Lista de contratos clickeables
 * → onRowClick(id). Se abre hacia abajo o hacia arriba según el espacio. */
export default function DrillPopover({ open, onClose, anchorRect, title, subtitle, accent = 'var(--pei-cyan)', rows = [], emptyText = 'Sin contratos.', onRowClick, onPointerStay, onPointerLeave }) {
  const popRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    const onDown = (e) => { if (popRef.current && !popRef.current.contains(e.target)) onClose?.(); };
    // Si la página (o cualquier contenedor externo) se desplaza, el ancla queda
    // desfasada: cerrar. Los scrolls DENTRO del popover se ignoran.
    const onScroll = (e) => { if (!popRef.current || !popRef.current.contains(e.target)) onClose?.(); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [open, onClose]);

  // Candado de scroll: la rueda sobre el popover NUNCA mueve el dashboard de
  // atrás. Si la lista interna puede consumir el delta, se le deja; si no hay
  // overflow o ya está en el tope, preventDefault (listener no-pasivo).
  useEffect(() => {
    if (!open) return undefined;
    const el = popRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      const list = listRef.current;
      const canScroll = list && list.scrollHeight > list.clientHeight + 1;
      if (!canScroll || !list.contains(e.target)) {
        e.preventDefault();
        return;
      }
      const down = e.deltaY > 0;
      const atTop = list.scrollTop <= 0;
      const atBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 1;
      if ((down && atBottom) || (!down && atTop)) e.preventDefault();
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [open]);

  let pos = { top: 80, left: 80, maxHeight: MAXH, openUp: false };
  if (anchorRect) {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const left = Math.min(Math.max(anchorRect.left, 12), vw - W - 12);
    const spaceBelow = vh - anchorRect.bottom;
    const openUp = spaceBelow < 320 && anchorRect.top > spaceBelow;
    pos = openUp
      ? { left, bottom: vh - anchorRect.top + 8, maxHeight: Math.min(MAXH, anchorRect.top - 16), openUp }
      : { left, top: anchorRect.bottom + 8, maxHeight: Math.min(MAXH, spaceBelow - 16), openUp };
  }

  const { openUp, maxHeight, ...place } = pos;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          ref={popRef}
          className="drill-pop"
          style={{ ...place, width: W }}
          initial={{ opacity: 0, y: openUp ? 8 : -8, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: openUp ? 8 : -8, scale: 0.98 }}
          transition={{ type: 'spring', stiffness: 420, damping: 30 }}
          role="dialog"
          onMouseEnter={onPointerStay}
          onMouseLeave={onPointerLeave}
        >
          <div className="drill-head" style={{ '--drill-accent': accent }}>
            <div>
              <div className="drill-title">{title}</div>
              {subtitle ? <div className="drill-sub">{subtitle}</div> : null}
            </div>
            <button type="button" className="drill-x" onClick={onClose} aria-label="Cerrar">
              <i className="pi pi-times" />
            </button>
          </div>

          <div className="drill-list" ref={listRef} style={{ maxHeight: Math.max(120, maxHeight - 56) }}>
            {rows.length === 0 ? (
              <div className="drill-empty">{emptyText}</div>
            ) : (
              rows.map((row) => (
                <button
                  type="button"
                  className="drill-row"
                  key={row.id}
                  onClick={() => { onRowClick?.(row.id); onClose?.(); }}
                >
                  <span className="ava-sm" style={{ background: avaColor(row.cliente || row.sub) }}>
                    {iniciales(row.cliente)}
                  </span>
                  <span className="drill-row-main">
                    <span className="nm">
                      {row.dotColor ? <span className="drill-dot" style={{ background: row.dotColor }} /> : null}
                      {row.cliente || '—'}
                    </span>
                    <span className="sub">{row.sub}</span>
                  </span>
                  <span className="drill-row-right">
                    <span className={'money' + (row.rightNeg ? ' neg' : '')}>{row.right}</span>
                    {row.rightSub ? <span className="sub">{row.rightSub}</span> : null}
                  </span>
                </button>
              ))
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
